import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { getDashboardNaoConformidade } from '../../../../../app/modules/helpers/Api';
import { toolboxDownload } from '../charts/toolboxDownload';
import { eixoCategoria } from '../charts/eixosBase';

const COR_BARRA = { fill: 'rgba(114, 57, 234, 0.35)', border: '#7239ea' };

interface LinhaFormulario {
    prescription_id: number;
    categoria: string | null;
    formulario: string;
    percentual: number;
    pontuacao_media: number;
    pontuacao_maxima: number;
    respostas: number;
}

interface Props {
    empresaId: number;
}

const PainelNaoConformidade: React.FC<Props> = ({ empresaId }) => {
    const [linhas, setLinhas] = useState<LinhaFormulario[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(false);

    const ref = React.useRef<any>(null);

    useEffect(() => {
        setErro(false);
        setCarregando(true);

        getDashboardNaoConformidade(empresaId)
            .then((response) => setLinhas(response.data.data.formularios || []))
            .catch(() => setErro(true))
            .finally(() => setCarregando(false));
    }, [empresaId]);

    const rotulo = (linha: LinhaFormulario) => (
        linha.categoria ? `${linha.categoria} — ${linha.formulario}` : linha.formulario
    );

    // Barra horizontal le de baixo para cima: inverter poe o maior no topo.
    const ordenadas = [...linhas].reverse();

    const opcao = () => ({
        title: {
            text: 'Não conformidade por formulário',
            subtext: 'Percentual do máximo de cada formulário — dez maiores',
            left: 'center',
            textStyle: { color: '#333', fontSize: 16 },
            subtextStyle: { color: '#7e8299', fontSize: 12 },
        },
        grid: { top: 70, bottom: 40, left: 260, right: 40 },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params: any[]) => {
                const linha = ordenadas[params[0].dataIndex];

                return [
                    rotulo(linha),
                    `Percentual: ${linha.percentual}%`,
                    `Pontuação média: ${linha.pontuacao_media} de ${linha.pontuacao_maxima}`,
                    `Respostas: ${linha.respostas}`,
                ].join('<br/>');
            },
        },
        xAxis: {
            type: 'value',
            name: '% do máximo',
            nameLocation: 'middle',
            nameTextStyle: { fontSize: 14, padding: [10, 0, 0, 0] },
            min: 0,
            splitLine: { show: true },
            axisLabel: { fontSize: 12, formatter: '{value}%' },
        },
        yAxis: {
            ...eixoCategoria('', ordenadas.map((linha) => rotulo(linha))),
            axisLabel: {
                fontSize: 11,
                interval: 0,
                width: 240,
                overflow: 'truncate' as 'truncate',
            },
        },
        series: [{
            type: 'bar',
            barWidth: '55%',
            data: ordenadas.map((linha) => ({
                value: linha.percentual,
                itemStyle: { color: COR_BARRA.fill, borderColor: COR_BARRA.border, borderWidth: 1 },
            })),
        }],
        toolbox: toolboxDownload(ref, 'nao-conformidade-por-formulario'),
    });

    if (carregando) {
        return (
            <div className='col-12 d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
                <span className='spinner-border spinner-border-lg align-middle'></span>
            </div>
        );
    }

    if (erro) {
        return (
            <div className='col-12 d-flex justify-content-center align-items-center' style={{ height: '200px' }}>
                <span className='text-gray-600 fw-bold fs-4'>Não foi possível carregar a não conformidade.</span>
            </div>
        );
    }

    if (linhas.length === 0) {
        return (
            <div className='col-12 d-flex justify-content-center align-items-center' style={{ height: '200px' }}>
                <span className='text-gray-600 fw-bold fs-4'>Esta empresa ainda não tem diagnóstico respondido.</span>
            </div>
        );
    }

    return (
        <div className='col-12 mb-10'>
            <ReactECharts
                ref={ref}
                option={opcao()}
                opts={{ renderer: 'svg' }}
                style={{ minHeight: `${Math.max(320, linhas.length * 46 + 120)}px`, width: '100%' }}
            />
        </div>
    );
};

export default PainelNaoConformidade;
